import { useState, useEffect, lazy, Suspense } from 'react'
import { initPushNotifications, removePushToken } from './PushNotifications'
import { supabase } from './supabase'
import { T, S, formatError } from './theme'
import { checkForAppUpdate } from './utils/checkAppUpdate'
import { trackSession } from './trackSession'

// Heavy screens load only when needed — keeps first load fast.
const CustomerHome = lazy(() => import('./CustomerHome'))
import CartNowBar from './CartNowBar'
import FloatingOrderBanner from './FloatingOrderBanner'
const OwnerHome = lazy(() => import('./OwnerHome'))
const OwnerHotelDashboard = lazy(() => import('./OwnerHotelDashboard'))
const OwnerOrderShell = lazy(() => import('./OwnerOrderShell'))
const OwnerHub = lazy(() => import('./OwnerHub'))
const AdminHome = lazy(() => import('./AdminHome'))
const DeliveryHome = lazy(() => import('./DeliveryHome'))
const EditProfile = lazy(() => import('./EditProfile'))

// Loading screen shown while a lazy screen downloads
function ScreenLoader() {
  return (
    <div style={{ minHeight: '100vh', background: '#080808', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px' }}>
      <img src="/SYNINN_bg_remove_logo.png" alt="SYNINN" style={{ width: '90px', opacity: 0.95, animation: 'synPulse 1.4s ease-in-out infinite' }} />
      <div style={{ width: '34px', height: '34px', border: '2px solid rgba(201,168,76,0.25)', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'synSpin 0.8s linear infinite' }} />
      <style>{`@keyframes synSpin{to{transform:rotate(360deg)}}@keyframes synPulse{0%,100%{opacity:0.55}50%{opacity:1}}`}</style>
    </div>
  )
}

function SuspendedScreen({ reason, onLogout }) {
  return (
    <div style={{ minHeight: '100vh', background: '#080808', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚫</div>
      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', color: '#C9A84C', marginBottom: '12px' }}>Account Suspended</div>
      <div style={{ fontSize: '14px', color: '#999', maxWidth: '340px', lineHeight: 1.6, marginBottom: reason ? '16px' : '24px' }}>
        Your account has been suspended and you cannot use SYNINN services at this time.
      </div>
      {reason && (
        <div style={{ fontSize: '13px', color: '#e0a94c', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '10px', padding: '12px 16px', maxWidth: '340px', marginBottom: '24px' }}>
          Reason: {reason}
        </div>
      )}
      <div style={{ fontSize: '13px', color: '#999', marginBottom: '8px' }}>Think this is a mistake? Contact us:</div>
      <a href="mailto:support@syninn.com" style={{ color: '#C9A84C', fontSize: '14px', textDecoration: 'none', marginBottom: '28px' }}>📧 support@syninn.com</a>
      <button onClick={onLogout} style={{ padding: '12px 32px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', color: '#999', fontSize: '14px', cursor: 'pointer' }}>Log Out</button>
    </div>
  )
}

export default function App() {
  const [signupSuccess, setSignupSuccess] = useState(false)
  const [otpMode, setOtpMode] = useState(false)       // showing the OTP entry screen
  const [otpEmail, setOtpEmail] = useState('')        // email the OTP was sent to
  const [otpToken, setOtpToken] = useState('')        // the 6-digit code the user types
  const [otpLoading, setOtpLoading] = useState(false)
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [appSettings, setAppSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authMode, setAuthMode] = useState('login')
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'customer', date_of_birth: '', referral_code: '', capabilities: [], phone: '' })
  const [error, setError] = useState('')
  const [authLoading, setAuthLoading] = useState(false)

  // Forgot password states
  const [forgotMode, setForgotMode] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSent, setForgotSent] = useState(false)
  const [forgotLoading, setForgotLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [resetPasswordMode, setResetPasswordMode] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [resetDone, setResetDone] = useState(false)
  const [screen, setScreen] = useState('home')
  const [ownerBusinessOverride, setOwnerBusinessOverride] = useState(null)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [slowNetwork, setSlowNetwork] = useState(false)


  // Load global app settings (maintenance, signup/order/hotel/dine-in toggles)
  useEffect(() => {
    let mounted = true
    const loadSettings = async () => {
      const { data } = await supabase.from('settings').select('maintenance_mode, maintenance_message, signups_open, registrations_open, orders_open, hotel_bookings_open, dine_in_open').eq('id', 1).maybeSingle()
      if (mounted && data) setAppSettings(data)
    }
    loadSettings()
    return () => { mounted = false }
  }, [])
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // If first load hasn't finished within 12s, treat the network as slow
  useEffect(() => {
    if (!loading) { setSlowNetwork(false); return }
    const timer = setTimeout(() => { if (loading) setSlowNetwork(true) }, 5000)
    return () => clearTimeout(timer)
  }, [loading])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session) fetchProfile(session.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // Handle password recovery event — session is active, show reset screen
      if (_event === 'PASSWORD_RECOVERY') {
        setSession(session)
        setResetPasswordMode(true)
        setLoading(false)
        return
      }
      setSession(session)
      if (session) {
        fetchProfile(session.user.id)
      } else { 
        setProfile(null)
        setLoading(false) 
      }
    })
 
    // Check for Play Store app update on launch
    checkForAppUpdate()

    // Reconnect Supabase realtime + re-check for update when app comes to foreground
    import('@capacitor/app').then(({ App: CapApp }) => {
      CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          supabase.realtime.connect()
          checkForAppUpdate()
        }
      })
    }).catch(() => { /* not native platform */ })

    // Android back button handler
    let lastBackPress = 0
    const handleBackButton = () => {
      // If there's real history (inside a flow), go back normally
      if (window.history.length > 1 && (Date.now() - lastBackPress > 2000)) {
        // Not a double-press: try normal back first
        const before = window.location.href
        window.history.back()
        // If back didn't change anything within a tick, treat as top-level
        setTimeout(() => {
          if (window.location.href === before) {
            const now = Date.now()
            if (now - lastBackPress < 2000) {
              if (window.navigator?.app) window.navigator.app.exitApp()
            } else {
              lastBackPress = now
            }
          }
        }, 60)
        return
      }
      // Double-press to exit
      const now = Date.now()
      if (now - lastBackPress < 2000) {
        if (window.navigator?.app) window.navigator.app.exitApp()
      } else {
        lastBackPress = now
      }
    }

    document.addEventListener('backbutton', handleBackButton)

    return () => {
      subscription.unsubscribe()
      document.removeEventListener('backbutton', handleBackButton)
    }
  }, [])

  const fetchProfile = async (userId) => {
    try {
      // Try by UUID first
      const { data } = await supabase
        .from('users').select('*').eq('id', userId).maybeSingle()
      if (data) {
        setProfile(data)
        setLoading(false)
        // Initialize push notifications after profile confirmed
        initPushNotifications(data.id)
        // Track app open with platform + location (fire-and-forget)
        trackSession(data.id)
        return
      }

      // Wait 2 seconds — trigger may still be running
      await new Promise(res => setTimeout(res, 2000))

      const { data: r2 } = await supabase
        .from('users').select('*').eq('id', userId).maybeSingle()
      if (r2) { setProfile(r2); setLoading(false); return }

      // UUID lookup failed — try by email as fallback
      // Handles case where old profile UUID doesn't match new auth UUID
      const { data: authData } = await supabase.auth.getUser()
      if (authData?.user?.email) {
        const { data: byEmail } = await supabase
          .from('users').select('*').eq('email', authData.user.email).maybeSingle()
        if (byEmail) { setProfile(byEmail); setLoading(false); return }
      }

      // All attempts failed — sign out cleanly
      await supabase.auth.signOut()
      setSession(null)
      setProfile(null)
      setError('Account setup incomplete. Please sign up again.')
      setLoading(false)

    } catch (err) {
      console.error('fetchProfile error:', err)
      setLoading(false)
    }
  }

  async function handleLogin(e) {
    e.preventDefault()
    setAuthLoading(true)
    setError('')
    const { error } = await supabase.auth.signInWithPassword({
      email: form.email,
      password: form.password,
    })
    setAuthLoading(false)
    if (error) {
      if (error.message?.includes('Email not confirmed')) {
        setError('Please confirm your email before logging in. Check your inbox for the confirmation link.')
      } else if (error.message?.includes('Invalid login credentials')) {
        setError('Incorrect email or password. Please try again or use Forgot Password.')
      } else {
        setError(formatError(error, 'Login failed. Please check your email and password.'))
      }
    }
  }

  async function handleSignup(e) {
    e.preventDefault()
    setAuthLoading(true)
    setError('')
    // Respect admin toggles for new signups / owner registrations
    if (form.role === 'owner' && appSettings && appSettings.registrations_open === false) {
      setError('New business registrations are temporarily closed. Please try again later.')
      setAuthLoading(false)
      return
    }
    if (form.role !== 'owner' && appSettings && appSettings.signups_open === false) {
      setError('New signups are temporarily closed. Please try again later.')
      setAuthLoading(false)
      return
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters')
      setAuthLoading(false)
      return
    }

    if (!form.name.trim()) {
      setError('Please enter your full name')
      setAuthLoading(false)
      return
    }

    // Capability selection moved to Register Business page (after login)
    if (!form.phone.trim() || form.phone.trim().length < 10) {
      setError('Please enter a valid 10-digit phone number')
      setAuthLoading(false)
      return
    }
    const emailDomain = form.email.trim().toLowerCase().split('@')[1] || ''
    const blockedDomains = ['aganseo.com', 'homephit.com', 'mailinator.com', 'tempmail.com', 'temp-mail.org', 'guerrillamail.com', 'guerrillamail.info', '10minutemail.com', 'yopmail.com', 'throwawaymail.com', 'getnada.com', 'trashmail.com', 'sharklasers.com', 'maildrop.cc', 'dispostable.com', 'fakeinbox.com', 'mailnesia.com', 'mohmal.com', 'emailondeck.com', 'tempinbox.com', 'moakt.com', 'mintemail.com', 'spamgourmet.com', 'mytemp.email', 'tempmailo.com']
    if (blockedDomains.includes(emailDomain)) {
      setError('Please use a real, permanent email address.')
      setAuthLoading(false)
      return
    }
    const { data, error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          name: form.name.trim(),
          role: form.role,
          date_of_birth: form.date_of_birth || '',
          phone: form.phone.trim(),
          referral_code: form.referral_code?.trim().toUpperCase() || '',
          requested_capabilities: form.role === 'owner' ? form.capabilities : [],
        }
      }
    })

    if (error) {
      // Supabase often returns "User already registered" for existing emails
      if ((error.message || '').toLowerCase().includes('already') || (error.message || '').toLowerCase().includes('registered')) {
        setError('This email is already registered. Please log in instead.')
      } else {
        setError(formatError(error, 'Could not create account. Please try again.'))
      }
      setAuthLoading(false)
      return
    }
    // Supabase security: for an already-registered (confirmed) email it may return
    // no error but an empty identities array. Treat that as "already registered".
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setError('This email is already registered. Please log in instead.')
      setAuthLoading(false)
      return
    }
    // Account created — now the user must enter the OTP sent to their email
    setOtpEmail(form.email)
    setOtpToken('')
    setOtpMode(true)
    setAuthLoading(false)
  }

  // Verify the 6-digit OTP the user received by email
  async function handleVerifyOtp() {
    if (!otpToken.trim() || otpToken.trim().length < 6) { setError('Please enter the 6-digit code'); return }
    setOtpLoading(true)
    setError('')
    const { error } = await supabase.auth.verifyOtp({
      email: otpEmail,
      token: otpToken.trim(),
      type: 'signup',
    })
    if (error) {
      setError(formatError(error, 'Invalid or expired code. Please try again.'))
      setOtpLoading(false)
      return
    }
    // Session is now active. The DB trigger may not have created the profile row,
    // so ensure it exists before onAuthStateChange tries to load it.
    try { await supabase.rpc('ensure_user_profile') } catch (e) { console.log('ensure_user_profile:', e) }
    // Success — profile is ready; onAuthStateChange will load it and take them inside
    setOtpMode(false)
    setOtpToken('')
    setForm({ name: '', email: '', password: '', role: 'customer', date_of_birth: '', referral_code: '', capabilities: [], phone: '' })
    setOtpLoading(false)
  }

  // Resend the OTP if it didn't arrive
  async function handleResendOtp() {
    setOtpLoading(true)
    setError('')
    const { error } = await supabase.auth.resend({ type: 'signup', email: otpEmail })
    if (error) setError(formatError(error, 'Could not resend code. Please wait a moment and try again.'))
    else setError('')
    setOtpLoading(false)
  }

  async function handleForgotPassword() {
    if (!forgotEmail.trim()) { setError('Please enter your email address'); return }
    setForgotLoading(true)
    setError('')
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
      redirectTo: 'https://syninn.com/account'
    })
    setForgotLoading(false)
    if (error) {
      setError(formatError(error, 'Could not send reset email. Please check your email address.'))
    } else {
      setForgotSent(true)
    }
  }

  async function handleLogout() {
    if (profile?.id) await removePushToken(profile.id)
    await supabase.auth.signOut()
    setProfile(null)
    setSession(null)
    setScreen('home')
    setOwnerBusinessOverride(null)
  }

  const handleProfileSaved = (newName) => {
    setProfile(prev => ({ ...prev, name: newName }))
  }

// Normalize /account to / for React
  // Don't redirect — Vercel handles /account routing via vercel.json

  // If the very first load can't finish AND we're offline, show a full retry screen.
  if (loading && !isOnline) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px', fontFamily: T.sans }}>
        <img src="/SYNINN_bg_remove_logo.png" alt="SYNINN" style={{ width: '80px', mixBlendMode: 'screen', opacity: 0.6, marginBottom: '24px' }} />
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📡</div>
        <div style={{ fontFamily: T.serif, fontSize: '24px', fontWeight: '300', color: T.white, letterSpacing: '2px', marginBottom: '8px' }}>No Connection</div>
        <div style={{ width: '24px', height: '1px', background: T.gold, margin: '0 auto 16px' }} />
        <div style={{ fontSize: '13px', color: T.muted, textAlign: 'center', lineHeight: '1.8', maxWidth: '280px' }}>
          Please check your internet connection and try again.
        </div>
        <button onClick={() => window.location.reload()} style={{ marginTop: '28px', padding: '12px 28px', background: T.goldFaint, border: `1px solid ${T.goldBorder}`, color: T.gold, borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontFamily: T.sans }}>
          Try Again
        </button>
      </div>
    )
  }
  // Loading is taking too long — likely a slow connection
  if (loading && slowNetwork) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px', fontFamily: T.sans, textAlign: 'center' }}>
        <img src="/SYNINN_bg_remove_logo.png" alt="SYNINN" style={{ width: '80px', mixBlendMode: 'screen', opacity: 0.6, marginBottom: '24px' }} />
        <div style={{ fontSize: '40px', marginBottom: '14px' }}>🐢</div>
        <div style={{ fontFamily: T.serif, fontSize: '22px', fontWeight: 300, color: T.white, letterSpacing: '1px', marginBottom: '8px' }}>Slow Connection</div>
        <div style={{ width: '24px', height: '1px', background: T.gold, margin: '0 auto 16px' }} />
        <div style={{ fontSize: '13px', color: T.muted, lineHeight: 1.7, maxWidth: '280px', marginBottom: '28px' }}>
          This is taking longer than usual. Please check your connection and try again.
        </div>
        <button onClick={() => window.location.reload()} style={{ padding: '13px 32px', background: T.goldFaint, border: `1px solid ${T.goldBorder}`, color: T.gold, borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: T.sans }}>
          Try Again
        </button>
      </div>
    )
  }
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px' }}>
        <img src="/SYNINN_bg_remove_logo.png" alt="SYNINN" style={{ width: '100px', height: 'auto', mixBlendMode: 'screen', opacity: 0.9, animation: 'synPulse 1.4s ease-in-out infinite' }} />
        <div style={{ width: '34px', height: '34px', border: '2px solid rgba(201,168,76,0.25)', borderTopColor: T.gold, borderRadius: '50%', animation: 'synSpin 0.8s linear infinite' }} />
        <style>{`@keyframes synSpin{to{transform:rotate(360deg)}}@keyframes synPulse{0%,100%{opacity:0.55}50%{opacity:1}}`}</style>
      </div>
    )
  }
  // Maintenance mode — block everyone except admin/master so they can turn it off
  if (appSettings?.maintenance_mode && profile?.role !== 'admin' && profile?.role !== 'master') {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px', padding: '32px', textAlign: 'center' }}>
        <img src="/SYNINN_bg_remove_logo.png" alt="SYNINN" style={{ width: '90px', height: 'auto', mixBlendMode: 'screen', opacity: 0.9 }} />
        <div style={{ fontSize: '44px' }}>🛠️</div>
        <div style={{ fontFamily: T.serif, fontSize: '24px', color: T.white }}>Under Maintenance</div>
        <div style={{ fontSize: '13px', color: T.muted, maxWidth: '340px', lineHeight: 1.6 }}>{appSettings.maintenance_message || 'We are making things better. Please check back shortly.'}</div>
        <button onClick={() => window.location.reload()} style={{ ...S.btnGold, marginTop: '8px' }}>Retry</button>
      </div>
    )
  }

  // Password reset screen — shown when user clicks reset link from email
  if (resetPasswordMode) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: T.sans }}>
        <div style={{ width: '100%', maxWidth: '420px', borderRadius: '20px', overflow: 'hidden', border: `1px solid ${T.goldBorder}`, background: `radial-gradient(80% 60% at 70% 8%, rgba(201,168,76,0.22), transparent 55%), radial-gradient(70% 50% at 20% 45%, rgba(201,168,76,0.10), transparent 55%), linear-gradient(160deg, #1a1610 0%, ${T.bg} 45%)` }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <img src="/SYNINN_bg_remove_logo.png" alt="SYNINN" style={{ height: '80px', mixBlendMode: 'screen' }} />
          </div>
          <div style={{ background: '#0f0f0f', border: `1px solid ${T.faint}`, borderRadius: '16px', padding: '32px' }}>
            {resetDone ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>✅</div>
                <div style={{ fontFamily: T.serif, fontSize: '20px', color: T.white, marginBottom: '8px' }}>Password Updated</div>
                <div style={{ fontSize: '12px', color: T.muted, marginBottom: '20px' }}>You can now log in with your new password.</div>
                <button onClick={() => { setResetPasswordMode(false); setResetDone(false); window.location.hash = '' }} style={{ ...S.btnGold, width: '100%', display: 'block' }}>Go to Login</button>
              </div>
            ) : (
              <div>
                <div style={{ fontFamily: T.serif, fontSize: '22px', color: T.white, marginBottom: '6px' }}>Set New Password</div>
                <div style={{ fontSize: '12px', color: T.muted, marginBottom: '24px' }}>Enter your new password below</div>
                {error && <div style={{ color: '#ff6b6b', fontSize: '13px', marginBottom: '16px', padding: '10px 14px', background: '#ff6b6b15', borderRadius: '8px' }}>{error}</div>}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: T.muted, fontSize: '13px', marginBottom: '4px', display: 'block' }}>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${T.faint}`, background: '#111', color: T.white, fontSize: '15px', fontFamily: T.sans, boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>
                <button
                  onClick={async () => {
                    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return }
                    setAuthLoading(true)
                    setError('')
                    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword })
                    setAuthLoading(false)
                    if (updateError) { setError(updateError.message); return }
                    setResetDone(true)
                  }}
                  disabled={authLoading}
                  style={{ ...S.btnGold, width: '100%', display: 'block', opacity: authLoading ? 0.7 : 1 }}
                >
                  {authLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Logged in — route to correct dashboard.
  // Wrapped in a helper so every lazy screen shares one Suspense fallback.
  if (session && profile) {
    return (
      <Suspense fallback={<ScreenLoader />}>
        {renderLoggedIn()}
        {profile.role === 'customer' && <CartNowBar onOpen={() => { window.location.hash = ''; localStorage.setItem('syninn_open_cart', '1'); window.location.reload() }} />}
        {profile.role === 'customer' && <FloatingOrderBanner user={profile} onTrack={(order) => { localStorage.setItem('syninn_open_order', order.id); window.location.reload() }} />}
      </Suspense>
    )
  }

  function renderLoggedIn() {
    if (profile.is_banned) {
      return <SuspendedScreen reason={profile.ban_reason} onLogout={handleLogout} />
    }
    if (screen === 'editProfile') {
      return (
        <EditProfile
          user={profile}
          onBack={() => setScreen('home')}
          onSaved={handleProfileSaved}
        />
      )
    }

    if (profile.role === 'delivery_partner') {
      return <DeliveryHome profile={profile} onLogout={handleLogout} />
    }

    if (profile.role === 'admin') {
      return <AdminHome profile={profile} onLogout={handleLogout} />
    }
    if (profile.role === 'owner') {
      return <OwnerHub profile={profile} onLogout={handleLogout} />
      // Determine which business dashboard to show
      const caps = Array.isArray(profile.approved_capabilities) ? profile.approved_capabilities : []
      const hasRestaurant = caps.includes('restaurant')
      const hasOrder = caps.includes('order')
      // canRestaurant means owner has a real restaurant (dine-in).
      // "order" alone is a cloud kitchen → handled by isOrderOnly check below.
      const canRestaurant = hasRestaurant
      const canHotel = caps.includes('hotel')
      // "Restaurant side" = dine-in restaurant OR order/cloud-kitchen
      const hasRestaurantSide = hasRestaurant || hasOrder
      // Owner can switch between hotel and restaurant-side if they have a hotel cap AND a restaurant-side cap
      const hasBoth = canHotel && hasRestaurantSide
      // Order-only owner (cloud kitchen): has 'order' but NOT 'restaurant' AND NOT 'hotel'
      const isOrderOnly = hasOrder && !hasRestaurant && !canHotel


      let currentBiz
      if (ownerBusinessOverride) {
        // User explicitly clicked a switch button this session
        currentBiz = ownerBusinessOverride
      } else if (canHotel && !canRestaurant) {
        currentBiz = 'hotel'
      } else if (canRestaurant && !canHotel) {
        currentBiz = 'restaurant'
      } else if (hasBoth) {
        // Owner has both — check last-used preference
        let last = null
        try { last = localStorage.getItem('syninn_last_business_' + profile.id) } catch {}
        currentBiz = last === 'hotel' ? 'hotel' : 'restaurant'
      } else {
        // No capabilities approved yet — fallback to restaurant (PendingApproval will show)
        currentBiz = 'restaurant'
      }

      // Switch handlers — save preference and update state
      const switchTo = (biz) => {
        try { localStorage.setItem('syninn_last_business_' + profile.id, biz) } catch {}
        setOwnerBusinessOverride(biz)
      }

      if (currentBiz === 'hotel') {
        return (
          <OwnerHotelDashboard
            profile={profile}
            onLogout={handleLogout}
            hasRestaurant={hasBoth}
            onSwitchToRestaurant={hasBoth ? () => switchTo('restaurant') : null}
          />
        )
      }

      // Cloud kitchen / order-only owner — give them the streamlined order dashboard
      if (isOrderOnly) {
        // Fetch the order-only restaurant first
        return <OwnerOrderShell profile={profile} onLogout={handleLogout} />
      }

      return (
        <OwnerHome
          profile={profile}
          onLogout={handleLogout}
          hasHotel={hasBoth}
          onSwitchToHotel={hasBoth ? () => switchTo('hotel') : null}
        />
      )
    }
    // ── Customer flow (default) ──
    // Known customer roles fall through here. An unknown/blank role is unexpected —
    // show a clean message instead of silently loading the customer app.
    const knownCustomer = !profile.role || profile.role === 'customer'
    if (knownCustomer) {
      return (
        <CustomerHome
          profile={profile}
          onLogout={handleLogout}
          appSettings={appSettings}
        />
      )
    }
    // Unrecognised role — access not available for this account
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px', fontFamily: T.sans, textAlign: 'center' }}>
        <img src="/SYNINN_bg_remove_logo.png" alt="SYNINN" style={{ width: '80px', mixBlendMode: 'screen', opacity: 0.6, marginBottom: '24px' }} />
        <div style={{ fontSize: '40px', marginBottom: '14px' }}>🚫</div>
        <div style={{ fontFamily: T.serif, fontSize: '24px', fontWeight: 300, color: T.white, letterSpacing: '1px', marginBottom: '8px' }}>Access Unavailable</div>
        <div style={{ width: '24px', height: '1px', background: T.gold, margin: '0 auto 16px' }} />
        <div style={{ fontSize: '13px', color: T.muted, lineHeight: 1.7, maxWidth: '300px', marginBottom: '28px' }}>
          This account doesn't have access here. Please contact support at support@syninn.com if you think this is a mistake.
        </div>
        <button onClick={handleLogout} style={{ padding: '13px 32px', background: T.goldFaint, border: `1px solid ${T.goldBorder}`, color: T.gold, borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: T.sans }}>
          Log out
        </button>
      </div>
    )
  }

  // Auth screen
  return (
    <div style={{
      minHeight: '100vh',
      background: T.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: T.sans,
    }}>
      <div style={{ width: '100%', maxWidth: '420px', borderRadius: '20px', overflow: 'hidden', border: `1px solid ${T.goldBorder}`, background: `radial-gradient(80% 60% at 70% 8%, rgba(201,168,76,0.22), transparent 55%), radial-gradient(70% 50% at 20% 45%, rgba(201,168,76,0.10), transparent 55%), linear-gradient(160deg, #1a1610 0%, ${T.bg} 45%)` }}>
       
        {/* Hero header */}
        <div style={{
          position: 'relative',
          padding: '40px 24px 28px',
          textAlign: 'center',
        }}>
          
          <img
            src="/SYNINN_bg_remove_logo.png"
            alt="SYNINN"
            style={{ height: '104px', mixBlendMode: 'screen' }}
          />
          <div style={{ fontFamily: T.serif, fontSize: '30px', fontWeight: 300, color: T.white, letterSpacing: '3px', marginTop: '8px' }}>
            SYNINN
          </div>
          <div style={{ width: '40px', height: '1px', background: T.goldBorder, margin: '14px auto 0' }} />
          <div style={{ fontSize: '9px', color: T.muted, letterSpacing: '4px', textTransform: 'uppercase', fontWeight: 500, marginTop: '14px' }}>
            Order · Stay · Dine
          </div>
        </div>

        {/* Card */}
        <div style={{ padding: '4px 28px 32px' }}>
          {otpMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '8px' }}>📧</div>
              <div style={{ fontFamily: T.serif, fontSize: '22px', color: T.white, marginBottom: '6px' }}>Check your email</div>
              <div style={{ fontSize: '13px', color: T.muted, lineHeight: 1.6 }}>We sent a 6-digit code to<br/><span style={{ color: T.gold }}>{otpEmail}</span></div>
            </div>
            <input
              type="text" inputMode="numeric" maxLength={6}
              value={otpToken}
              onChange={e => setOtpToken(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="000000"
              style={{ width: '100%', padding: '16px', borderRadius: '10px', border: `1px solid ${T.faint}`, background: '#111', color: T.white, fontSize: '28px', letterSpacing: '10px', textAlign: 'center', fontFamily: T.sans, boxSizing: 'border-box', outline: 'none' }}
            />
            {error && <div style={{ color: '#ff6b6b', fontSize: '13px', padding: '10px 14px', background: '#ff6b6b15', borderRadius: '8px' }}>{error}</div>}
            <button onClick={handleVerifyOtp} disabled={otpLoading} style={{ width: '100%', padding: '14px', borderRadius: '10px', border: 'none', background: T.gold, color: '#080808', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: T.sans, letterSpacing: '0.5px' }}>
              {otpLoading ? 'Verifying...' : 'Verify & Continue'}
            </button>
            <div style={{ textAlign: 'center', fontSize: '12px', color: T.muted }}>
              Didn't get it? <span onClick={handleResendOtp} style={{ color: T.gold, cursor: 'pointer', fontWeight: 600 }}>Resend code</span>
            </div>
            <div style={{ textAlign: 'center', fontSize: '12px', color: T.dimmed, cursor: 'pointer' }} onClick={() => { setOtpMode(false); setError(''); setAuthMode('signup') }}>← Use a different email</div>
          </div>
          ) : (
          <>

          {/* Tab switcher — hidden in forgot mode */}
          {!forgotMode && (
            <div style={{ display: 'flex', marginBottom: '28px', borderBottom: `1px solid ${T.faint}` }}>
              {['login', 'signup'].map(mode => (
                <button key={mode} onClick={() => { setAuthMode(mode); setError(''); setSignupSuccess(false) }} style={{
                  flex: 1, padding: '12px 0 14px', border: 'none', cursor: 'pointer',
                  background: 'transparent',
                  borderBottom: `2px solid ${authMode === mode ? T.gold : 'transparent'}`,
                  marginBottom: '-1px',
                  color: authMode === mode ? T.gold : T.muted,
                  fontFamily: T.sans, fontWeight: 600, fontSize: '12px',
                  letterSpacing: '1.5px', textTransform: 'uppercase', transition: 'all 0.2s'
                }}>
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                </button>
              ))}
            </div>
          )}

          {/* Forgot password header */}
          {forgotMode && (
            <div style={{ marginBottom: '24px' }}>
              <button onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); setError('') }} style={{ background: 'transparent', border: 'none', color: T.muted, fontSize: '13px', cursor: 'pointer', fontFamily: T.sans, padding: 0, marginBottom: '12px' }}>
                ← Back to Login
              </button>
              <div style={{ fontFamily: T.serif, fontSize: '22px', color: T.white, letterSpacing: '1px' }}>Reset Password</div>
              <div style={{ fontSize: '12px', color: T.muted, marginTop: '4px' }}>Enter your email and we will send you a reset link</div>
            </div>
          )}

          {/* Success message */}
          {signupSuccess && (
            <div style={{ color: '#4caf7d', fontSize: '13px', marginBottom: '16px', padding: '10px 14px', background: 'rgba(76,175,125,0.1)', borderRadius: '8px', border: '1px solid rgba(76,175,125,0.25)', lineHeight: '1.6' }}>
              ✓ Account created! Please check your email and confirm your account before logging in. If you don't see it, check your spam or junk folder and mark it "Not spam".
            </div>
          )}
          {/* Error message */}
          {error && (
            <div style={{ color: '#ff6b6b', fontSize: '13px', marginBottom: '16px', padding: '10px 14px', background: '#ff6b6b15', borderRadius: '8px' }}>
              {error}
            </div>
          )}

          {/* Forgot password form */}
          {forgotMode && !forgotSent && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ color: T.muted, fontSize: '13px', marginBottom: '4px', display: 'block', fontFamily: T.sans }}>Email Address</label>
                <input
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${T.faint}`, background: '#111', color: T.white, fontSize: '15px', fontFamily: T.sans, boxSizing: 'border-box', outline: 'none' }}
                  type="email"
                  placeholder="your@email.com"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  autoComplete="email"
                  onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
                />
              </div>
              <button
                onClick={handleForgotPassword}
                disabled={forgotLoading}
                style={{ ...S.btnGold, width: '100%', display: 'block', opacity: forgotLoading ? 0.7 : 1 }}
              >
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
          )}

          {/* Forgot password — email sent success */}
          {forgotMode && forgotSent && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>✉️</div>
              <div style={{ fontFamily: T.serif, fontSize: '18px', color: T.white, marginBottom: '8px' }}>Check your email</div>
              <div style={{ fontSize: '13px', color: T.muted, lineHeight: '1.7', marginBottom: '20px' }}>
                We sent a password reset link to<br />
                <span style={{ color: T.gold }}>{forgotEmail}</span><br />
                Click the link in the email to reset your password.
              </div>
              <div style={{ fontSize: '11px', color: T.dimmed, marginBottom: '20px', lineHeight: '1.6' }}>
                Give it a minute to arrive. If it's not in your inbox, check your spam or junk folder and mark it "Not spam" so future emails reach you.
              </div>
              <button onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); setError('') }} style={{ ...S.btnGold, width: '100%', display: 'block' }}>
                Back to Login
              </button>
            </div>
          )}

          {/* Normal login / signup form */}
          {!forgotMode && (
            <form onSubmit={authMode === 'login' ? handleLogin : handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {authMode === 'signup' && (
                <>
                  <div>
                    <label style={{ color: T.muted, fontSize: '13px', marginBottom: '4px', display: 'block', fontFamily: T.sans }}>Full Name</label>
                    <input
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${T.faint}`, background: '#111', color: T.white, fontSize: '15px', fontFamily: T.sans, boxSizing: 'border-box', outline: 'none' }}
                      placeholder="Your full name"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      maxLength={80}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ color: T.muted, fontSize: '13px', marginBottom: '4px', display: 'block', fontFamily: T.sans }}>I am a</label>
                    <select
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${T.faint}`, background: '#111', color: T.white, fontSize: '15px', fontFamily: T.sans, boxSizing: 'border-box', outline: 'none', cursor: 'pointer' }}
                      value={form.role}
                      onChange={e => setForm({ ...form, role: e.target.value, capabilities: e.target.value === 'owner' ? form.capabilities : [] })}
                    >
                      <option value="customer">Customer</option>
                      <option value="owner">Business Owner</option>
                      <option value="delivery_partner">Delivery Partner</option>
                    </select>
                  </div>

                  {/* Phone — for everyone */}
                  <div>
                    <label style={{ color: T.muted, fontSize: '13px', marginBottom: '4px', display: 'block', fontFamily: T.sans }}>Phone number</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, '').slice(0, 10) })}
                      placeholder="10-digit mobile number"
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${T.faint}`, background: '#111', color: T.white, fontSize: '15px', fontFamily: T.sans, boxSizing: 'border-box', outline: 'none' }}
                    />
                    <div style={{ color: T.dimmed, fontSize: '11px', marginTop: '4px' }}>We'll use this for order updates and to reach you.</div>
                  </div>
                  {/* Capability picker moved to Register Business page (after login) */}
                  {false && (
                  <div>
                    <label style={{ color: T.muted, fontSize: '13px', marginBottom: '8px', display: 'block', fontFamily: T.sans }}>
                      Which services will you offer? <span style={{ color: T.dimmed, fontSize: '11px' }}>(pick 1 or more)</span>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { id: 'restaurant', icon: '🍽', label: 'Restaurant', desc: 'Table bookings and dine-in' },
                        { id: 'order', icon: '🍱', label: 'Order Food', desc: 'Delivery and takeaway' },
                        { id: 'hotel', icon: '🏨', label: 'Hotel', desc: 'Room bookings and stays' },
                        { id: 'tiffin', icon: '🍲', label: 'Tiffin / Mess', desc: 'Subscription meal service' },
                      ].map(cap => {
                        const checked = form.capabilities.includes(cap.id)
                        return (
                          <button
                            type="button"
                            key={cap.id}
                            onClick={() => {
                              const next = checked
                                ? form.capabilities.filter(c => c !== cap.id)
                                : [...form.capabilities, cap.id]
                              setForm({ ...form, capabilities: next })
                            }}
                            style={{
                              padding: '12px 14px',
                              background: checked ? T.goldFaint : '#111',
                              border: `1px solid ${checked ? T.goldBorder : T.faint}`,
                              borderRadius: '10px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              textAlign: 'left',
                              fontFamily: T.sans,
                              transition: 'all 0.2s',
                            }}
                          >
                            <div style={{
                              width: '20px', height: '20px',
                              borderRadius: '6px',
                              border: `1.5px solid ${checked ? T.gold : T.faint}`,
                              background: checked ? T.gold : 'transparent',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#000', fontSize: '13px', fontWeight: '700', flexShrink: 0,
                            }}>{checked ? '✓' : ''}</div>
                            <div style={{ fontSize: '22px', flexShrink: 0 }}>{cap.icon}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', color: checked ? T.gold : T.white, fontWeight: '500' }}>{cap.label}</div>
                              <div style={{ fontSize: '11px', color: T.muted, marginTop: '2px' }}>{cap.desc}</div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                    <div style={{ fontSize: '11px', color: T.dimmed, marginTop: '8px', lineHeight: '1.5' }}>
                      ◆ Admin reviews each service request. You'll be notified when approved.
                    </div>
                  </div>
                  )}

                  <div>
                    <label style={{ color: T.muted, fontSize: '13px', marginBottom: '4px', display: 'block', fontFamily: T.sans }}>Date of Birth</label>
                    <input
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${T.faint}`, background: '#111', color: T.white, fontSize: '15px', fontFamily: T.sans, boxSizing: 'border-box', outline: 'none' }}
                      type="date"
                      value={form.date_of_birth}
                      onChange={e => setForm({ ...form, date_of_birth: e.target.value })}
                      max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                      required
                    />
                    <div style={{ color: T.muted, fontSize: '11px', marginTop: '4px' }}>Must be 18 or older to register</div>
                  </div>
                  <div>
                    <label style={{ color: T.muted, fontSize: '13px', marginBottom: '4px', display: 'block', fontFamily: T.sans }}>Referral Code <span style={{ color: T.dimmed, fontSize: '11px' }}>(optional)</span></label>
                    <input
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${T.faint}`, background: '#111', color: T.white, fontSize: '15px', fontFamily: T.sans, boxSizing: 'border-box', outline: 'none' }}
                      placeholder="e.g. ABC123"
                      value={form.referral_code || ''}
                      onChange={e => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                      maxLength={20}
                    />
                  </div>
                </>
              )}

              <div>
                <label style={{ color: T.muted, fontSize: '13px', marginBottom: '4px', display: 'block', fontFamily: T.sans }}>Email</label>
                <input
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${T.faint}`, background: '#111', color: T.white, fontSize: '15px', fontFamily: T.sans, boxSizing: 'border-box', outline: 'none' }}
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  maxLength={200}
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label style={{ color: T.muted, fontSize: '13px', marginBottom: '4px', display: 'block', fontFamily: T.sans }}>
                  Password {authMode === 'signup' && <span style={{ color: T.dimmed, fontSize: '11px' }}>(min 8 characters)</span>}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    style={{ width: '100%', padding: '12px 48px 12px 16px', borderRadius: '10px', border: `1px solid ${T.faint}`, background: '#111', color: T.white, fontSize: '15px', fontFamily: T.sans, boxSizing: 'border-box', outline: 'none' }}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    minLength={authMode === 'signup' ? 8 : undefined}
                    autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: T.muted, cursor: 'pointer', fontSize: '18px', padding: 0, lineHeight: 1 }}
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              {/* Forgot password link — login tab only */}
              {authMode === 'login' && (
                <div style={{ textAlign: 'right', marginTop: '-8px' }}>
                  <button
                    type="button"
                    onClick={() => { setForgotMode(true); setForgotEmail(form.email); setError('') }}
                    style={{ background: 'transparent', border: 'none', color: T.gold, fontSize: '12px', cursor: 'pointer', fontFamily: T.sans, padding: 0 }}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={authLoading}
                style={{ ...S.btnGold, width: '100%', display: 'block', marginTop: '8px', height: '48px', fontSize: '12px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', opacity: authLoading ? 0.7 : 1 }}
              >
                {authLoading ? 'Please wait...' : authMode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>
          )}
          </>
          )}
        </div>
        
        {/* Footer note */}
        {!forgotMode && (
          <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '11px', color: T.dimmed, lineHeight: '1.6' }}>
            By continuing you agree to our{' '}
            <a href="/terms-and-conditions" style={{ color: T.gold, textDecoration: 'none' }}>Terms</a>
            {' '}and{' '}
            <a href="/privacy-policy" style={{ color: T.gold, textDecoration: 'none' }}>Privacy Policy</a>
          </div>
        )}
      </div>
    </div>
  )
}
